import { Suspense, useMemo, useState } from 'react'
import { motion } from 'motion/react'
import { ScreenShell } from '@/components/layout/ScreenShell'
import { GameButton } from '@/components/ui/GameButton'
import { PixelIcon } from '@/components/ui/PixelIcon'
import { Tooltip, TooltipBody } from '@/components/ui/Tooltip'
import { EmberPreview } from '@/components/game3d/EmberPreview'
import { useProgress } from '@/contexts/ProgressProvider'
import { useShop } from '@/contexts/ShopProvider'
import { COSMETICS } from '@/game/catalog'
import { CHARACTERS, CHARACTER_ABILITIES, RARITY_COLORS, getCharacter, type CharacterDef } from '@/game/characters'
import { characterArt } from '@/game/art'
import { ArtImage } from '@/components/ui/ArtImage'
import { PROP_SLOTS, getProp } from '@/game/props'
import { aggregateEffects } from '@/game/propEffects'
import { useGameAudio } from '@/contexts/GameAudioProvider'

export function CharactersScreen() {
  const { save, update } = useProgress()
  const { playerLevel, buyCharacter, equipCharacter, equipProp, unequipProp } = useShop()
  const audio = useGameAudio()

  const equippedChar = getCharacter(save.cosmetics.equippedCharacter) ?? CHARACTERS[0]
  const [previewChar, setPreviewChar] = useState<CharacterDef>(equippedChar)

  const skins = COSMETICS.filter((c) => c.kind === 'skin')
  const trails = COSMETICS.filter((c) => c.kind === 'trail')
  const equippedTrail = COSMETICS.find((c) => c.id === save.cosmetics.equippedTrail && c.kind === 'trail')
  const trailColor = equippedTrail && equippedTrail.id !== 'none' ? equippedTrail.color : null
  const reducedMotion = save.meta.settings.reducedMotion

  const skinColor = COSMETICS.find((c) => c.id === save.cosmetics.equippedSkin && c.kind === 'skin')?.color
  const previewColor =
    previewChar.id !== 'default_hero' ? previewChar.palette.base : (skinColor ?? previewChar.palette.base)

  const effects = useMemo(
    () => aggregateEffects(save.cosmetics.equippedProps, equippedChar),
    [save.cosmetics.equippedProps, equippedChar],
  )

  const equipSkin = (id: string) => {
    if (!save.cosmetics.ownedSkins.includes(id)) return
    audio.play('confirm')
    update((s) => ({ ...s, cosmetics: { ...s.cosmetics, equippedSkin: id } }))
  }
  const equipTrail = (id: string) => {
    if (!save.cosmetics.ownedTrails.includes(id)) return
    audio.play('confirm')
    update((s) => ({ ...s, cosmetics: { ...s.cosmetics, equippedTrail: id } }))
  }

  const characterAction = (c: CharacterDef) => {
    const owned = save.cosmetics.ownedCharacters.includes(c.id)
    if (owned) {
      if (equipCharacter(c.id)) audio.play('confirm')
      return
    }
    if (buyCharacter(c.id)) audio.play('levelup')
    else audio.play('error')
  }

  const characterLabel = (c: CharacterDef) => {
    const owned = save.cosmetics.ownedCharacters.includes(c.id)
    if (save.cosmetics.equippedCharacter === c.id) return 'Equipped'
    if (owned) return 'Equip'
    if (c.iapOnly) return 'Store exclusive'
    if (c.unlockLevel != null && playerLevel >= c.unlockLevel) return 'Claim'
    if (c.price?.gems) return `${c.price.gems} gems`
    if (c.price?.coins) return `${c.price.coins} coins`
    return `Level ${c.unlockLevel}`
  }

  return (
    <ScreenShell title="Characters">
      <div className="flex flex-col items-center gap-5">
        <div
          className="relative grid place-items-center w-full max-w-xs aspect-square rounded-[var(--r-xl)] overflow-hidden shadow-sunken border border-[var(--edge)]/40"
          style={{ background: 'radial-gradient(circle at 50% 38%, var(--surface-raised), var(--surface-sunken))' }}
        >
          <Suspense fallback={<span className="text-sm text-[var(--text-faint)]">Loading preview…</span>}>
            <EmberPreview
              color={previewColor}
              trailColor={trailColor}
              reducedMotion={reducedMotion}
              shape={previewChar.shape}
              characterId={previewChar.id}
              equippedProps={previewChar.id === save.cosmetics.equippedCharacter ? save.cosmetics.equippedProps : []}
            />
          </Suspense>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/40 px-3 py-1 text-xs font-semibold text-[var(--text)] backdrop-blur">
            {previewChar.name}
          </div>
          <div
            className="absolute top-3 left-3 rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wide"
            style={{ background: RARITY_COLORS[previewChar.rarity], color: '#16121f' }}
          >
            {previewChar.rarity}
          </div>
        </div>

        <div className="w-full">
          <h2 className="font-display font-bold mb-2 text-[var(--text-dim)]">Heroes</h2>
          <div className="grid grid-cols-3 gap-2">
            {CHARACTERS.map((c) => {
              const owned = save.cosmetics.ownedCharacters.includes(c.id)
              const equipped = save.cosmetics.equippedCharacter === c.id
              const art = characterArt(c.id)
              return (
                <Tooltip
                  key={c.id}
                  className="w-full"
                  content={
                    <TooltipBody
                      title={`${c.name} (${c.rarity})`}
                      lines={[c.description, ...c.abilities.map((a) => CHARACTER_ABILITIES[a].effectDescription)]}
                    />
                  }
                >
                <motion.button
                  whileTap={{ scale: 0.96 }}
                  onClick={() => setPreviewChar(c)}
                  className={[
                    'relative w-full flex flex-col items-center gap-1 rounded-[var(--r-md)] p-2 shadow-emboss transition border',
                    equipped ? 'ring-2 ring-[var(--ember)]' : '',
                    previewChar.id === c.id ? 'border-[var(--text-dim)]/50' : 'border-transparent',
                    owned ? '' : 'opacity-70',
                  ].join(' ')}
                  style={{ background: 'var(--surface-raised)' }}
                  aria-label={c.name}
                >
                  <span
                    className="relative aspect-square w-full overflow-hidden rounded-[var(--r-sm)]"
                    style={{
                      background: `radial-gradient(circle at 50% 40%, ${c.palette.base}22, var(--surface-sunken))`,
                      boxShadow: `inset 0 0 0 2px ${RARITY_COLORS[c.rarity]}55`,
                    }}
                  >
                    {art ? (
                      <ArtImage
                        src={art}
                        alt={c.name}
                        className="h-full w-full"
                        fallbackColor={c.palette.base}
                        reducedMotion={reducedMotion}
                      />
                    ) : (
                      <span
                        className="absolute left-1/2 top-1/2 h-9 w-9 -translate-x-1/2 -translate-y-1/2 rounded-full"
                        style={{ background: `radial-gradient(circle at 35% 30%, ${c.palette.emissive}, ${c.palette.base} 65%)` }}
                      />
                    )}
                    {!owned && (
                      <span className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-black/50 text-[var(--text-dim)] backdrop-blur">
                        <PixelIcon name="lock" size={11} />
                      </span>
                    )}
                    {equipped && (
                      <span className="absolute bottom-1 left-1 rounded-full bg-[var(--ember)] px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide text-[#231404]">
                        On
                      </span>
                    )}
                  </span>
                  <span className="text-[11px] font-semibold leading-tight">{c.name}</span>
                  <span className="flex gap-0.5">
                    {c.abilities.map((a) => (
                      <span key={a} title={CHARACTER_ABILITIES[a].name} style={{ color: CHARACTER_ABILITIES[a].color }}>
                        <PixelIcon name={CHARACTER_ABILITIES[a].icon} size={11} />
                      </span>
                    ))}
                  </span>
                </motion.button>
                </Tooltip>
              )
            })}
          </div>
          <div className="mt-2 flex flex-col gap-1.5">
            <GameButton
              size="sm"
              fullWidth
              silent
              disabled={
                save.cosmetics.equippedCharacter === previewChar.id ||
                (!save.cosmetics.ownedCharacters.includes(previewChar.id) &&
                  (previewChar.iapOnly ||
                    (previewChar.unlockLevel != null && playerLevel < previewChar.unlockLevel && !previewChar.price)))
              }
              onClick={() => characterAction(previewChar)}
            >
              {characterLabel(previewChar)}
            </GameButton>
            <p className="text-center text-xs text-[var(--text-faint)]">
              {previewChar.abilities.map((a) => CHARACTER_ABILITIES[a].effectDescription).join(' · ')}
            </p>
          </div>
        </div>

        <div className="w-full">
          <h2 className="font-display font-bold mb-2 text-[var(--text-dim)]">Props</h2>
          {save.cosmetics.ownedProps.length === 0 ? (
            <p className="rounded-[var(--r-md)] bg-[var(--surface-sunken)] px-4 py-3 text-center text-sm text-[var(--text-faint)]">
              No props yet — visit the Shop to gear up Ember.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {PROP_SLOTS.map((slot) => {
                const ownedInSlot = save.cosmetics.ownedProps
                  .map((id) => getProp(id))
                  .filter((p): p is NonNullable<typeof p> => p != null && p.slot === slot)
                if (ownedInSlot.length === 0) return null
                const equippedId = save.cosmetics.equippedProps.find((id) => getProp(id)?.slot === slot)
                return (
                  <div key={slot} className="rounded-[var(--r-md)] bg-[var(--surface)] shadow-emboss border border-[var(--edge)]/30 p-3">
                    <div className="mb-2 text-xs font-bold uppercase tracking-wide text-[var(--text-faint)]">{slot}</div>
                    <div className="flex flex-wrap gap-2">
                      {ownedInSlot.map((p) => {
                        const isEquipped = equippedId === p.id
                        return (
                          <Tooltip key={p.id} content={<TooltipBody title={p.name} lines={p.effects.map((e) => e.description)} />}>
                          <button
                            onClick={() => {
                              if (isEquipped) {
                                if (unequipProp(p.id)) audio.play('back')
                              } else if (equipProp(p.id)) audio.play('confirm')
                            }}
                            className={[
                              'flex items-center gap-2 rounded-[var(--r-sm)] px-2.5 py-1.5 text-xs font-semibold shadow-emboss transition',
                              isEquipped ? 'ring-2 ring-[var(--ember)] text-[var(--text)]' : 'text-[var(--text-dim)]',
                            ].join(' ')}
                            style={{ background: 'var(--surface-raised)' }}
                          >
                            <span style={{ color: p.palette.glow }}>
                              <PixelIcon name={p.icon} size={14} />
                            </span>
                            {p.name}
                          </button>
                          </Tooltip>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
              <p className="text-center text-xs text-[var(--text-faint)]">
                Active: coins x{effects.coinMult} · score x{effects.scoreMult}
                {effects.magnetRadius > 0 ? ` · magnet ${effects.magnetRadius}` : ''}
                {effects.oneHitShield ? ' · shield' : ''}
                {effects.slowmoTint ? ' · slow-mo' : ''}
                {effects.slideSpeedMult > 1 ? ` · speed x${effects.slideSpeedMult}` : ''}
              </p>
            </div>
          )}
        </div>

        <div className="w-full">
          <h2 className="font-display font-bold mb-2 text-[var(--text-dim)]">Skins</h2>
          <div className="grid grid-cols-4 gap-2">
            {skins.map((c) => {
              const owned = save.cosmetics.ownedSkins.includes(c.id)
              const equipped = save.cosmetics.equippedSkin === c.id
              return (
                <button
                  key={c.id}
                  onClick={() => equipSkin(c.id)}
                  disabled={!owned}
                  className={[
                    'relative aspect-square rounded-[var(--r-md)] grid place-items-center shadow-emboss transition',
                    equipped ? 'ring-2 ring-[var(--ember)]' : '',
                    owned ? '' : 'opacity-50',
                  ].join(' ')}
                  style={{ background: 'var(--surface-raised)' }}
                  aria-label={c.name}
                  title={c.name}
                >
                  <span className="w-8 h-8 rounded-full" style={{ background: c.color }} />
                </button>
              )
            })}
          </div>
        </div>

        <div className="w-full">
          <h2 className="font-display font-bold mb-2 text-[var(--text-dim)]">Trails</h2>
          <div className="grid grid-cols-3 gap-2">
            {trails.map((c) => {
              const owned = save.cosmetics.ownedTrails.includes(c.id)
              const equipped = save.cosmetics.equippedTrail === c.id
              return (
                <button
                  key={c.id}
                  disabled={!owned}
                  onClick={() => equipTrail(c.id)}
                  className={[
                    'rounded-[var(--r-md)] p-3 shadow-emboss text-sm font-semibold transition',
                    equipped ? 'ring-2 ring-[var(--ember)]' : '',
                    owned ? 'text-[var(--text)]' : 'text-[var(--text-faint)] opacity-60',
                  ].join(' ')}
                  style={{ background: 'var(--surface-raised)' }}
                >
                  {c.name}
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </ScreenShell>
  )
}
